import { createContext, useContext } from "react";

export const LangContext = createContext({ lang: "fr", setLang: () => {}, t: {} });
export const useLang = () => useContext(LangContext);

export const translations = {
  fr: {
    nav: {
      home: "Accueil",
      about: "À propos",
      products: "Nos produits",
      trade: "Import / Export",
      contact: "Contact",
      cta: "Nous contacter",
      menu: "Menu",
      close: "Fermer",
    },
    hero: {
      overline: "Négoce international — Matières premières & produits surgelés",
      lines: ["Sourcing, négoce,", "livraison : une chaîne", "maîtrisée de bout en bout."],
      sub: "Des matières premières agricoles aux produits surgelés, ORIS INTERNATIONAL sécurise vos approvisionnements et accompagne vos opérations d'import-export à l'international.",
      ctaPrimary: "Découvrir nos produits",
      ctaSecondary: "Nous contacter",
      scroll: "Défiler",
      stats: [
        { value: "2015", label: "Année de création" },
        { value: "50+", label: "Coopératives partenaires" },
        { value: "2", label: "Bureaux — Abidjan & Marignane" },
        { value: "-25°C", label: "Chaîne du froid contrôlée" },
      ],
    },
    marquee: [
      "Import / Export",
      "Qualité certifiée",
      "Traçabilité complète",
      "Chaîne du froid maîtrisée",
      "Développement durable",
      "Délais respectés",
    ],
    about: {
      overline: "À propos — Notre engagement",
      title: "Une aventure commerciale au service de l'Afrique et du monde",
      intro:
        "Depuis sa création en septembre 2015, la société ORIS INTERNATIONAL s'est donné une mission simple mais exigeante : rapprocher les producteurs, les fournisseurs et les acheteurs internationaux autour d'une même exigence de confiance et de qualité.\n\nTout commence par la sélection rigoureuse des produits à la source, directement auprès des filières locales que nous connaissons et suivons de près. Vient ensuite le travail de terrain : contrôle qualité, négociation, structuration des transactions — chaque étape est pensée pour sécuriser nos partenaires.\n\nNotre expertise ne s'arrête pas là. Nous accompagnons chaque cargaison jusqu'à sa destination finale, en maîtrisant les enjeux logistiques et douaniers propres au commerce international — import comme export.\n\nC'est cette maîtrise de bout en bout, associée à une connaissance fine des filières et à une exigence constante de transparence, qui nous permet aujourd'hui d'offrir à nos partenaires des échanges commerciaux fiables et durables.",
      chapters: [
        {
          n: "01",
          title: "Notre mission",
          text: "Sourcing, achat et revente de matières premières à l'international. Nous sélectionnons et commercialisons des matières premières agricoles et des produits destinés aux marchés internationaux, notamment le maïs, la noix de cajou, le café, le cacao, le karité et la coque de cajou, tout en développant une activité dédiée aux produits surgelés. De l'approvisionnement à la livraison, nous coordonnons les opérations avec rigueur afin de garantir fiabilité, continuité d'approvisionnement et respect des engagements commerciaux.",
        },
        {
          n: "02",
          title: "Notre exigence qualité",
          text: "La qualité commence dès l'origine. Nous accordons une attention particulière à la sélection des produits, à leur conformité et à leurs conditions de stockage et de transport. Selon les marchandises et les exigences du marché de destination, nous veillons à la mise en place des contrôles qualité, documents de conformité, inspections et conditions de conservation nécessaires, notamment pour les produits soumis à la chaîne du froid.",
        },
        {
          n: "03",
          title: "Notre savoir-faire logistique",
          text: "Maîtriser chaque étape du parcours. Transport maritime, conteneurs conventionnels ou réfrigérés, coordination documentaire et suivi des expéditions : nous travaillons à fluidifier chaque opération afin de réduire les risques et de sécuriser les délais.",
        },
        {
          n: "04",
          title: "Notre vision",
          text: "Construire des relations commerciales qui durent. Pour ORIS INTERNATIONAL, le négoce ne se limite pas à une transaction. Nous privilégions des partenariats fondés sur la transparence, la réactivité, la confiance et la création de valeur à long terme.",
        },
      ],
      imageCaption: "Logistique & export — chaque cargaison sécurisée",
    },
    products: {
      overline: "Nos produits",
      title: "De la production au commerce international",
      intro:
        "Nous travaillons avec une cinquantaine de coopératives sur toute l'étendue du territoire. Deux filières, une même exigence : la qualité à l'origine, la sécurité au long du voyage.",
      rawLabel: "Matières premières agricoles",
      fiche: "Voir la fiche",
      raw: [
        { key: "mais", name: "Maïs", desc: "Maïs grain destiné à l'alimentation humaine et animale, séché et calibré selon vos spécifications." },
        { key: "cajou", name: "Noix de cajou", desc: "Noix de cajou brutes en coque, collectées auprès des producteurs de Côte d'Ivoire." },
        { key: "cafe", name: "Café", desc: "Arabica et robusta, lots tracés jusqu'à la coopérative, profils de tasse contrôlés." },
        { key: "cacao", name: "Cacao", desc: "Fèves fermentées et séchées, qualité fine de saveur, certification disponible sur demande." },
        { key: "riz", name: "Riz", desc: "Riz blanc, étuvé et parfumé, conditionné en sacs de 25 à 50 kg pour l'export." },
        { key: "karite", name: "Graine de karité pure", desc: "Graines de karité pures, issues d'une filière équitable de Côte d'Ivoire." },
        { key: "coque", name: "Coque de cajou", desc: "Coques de noix de cajou pour valorisation énergétique et industrielle (CNSL)." },
      ],
      frozenLabel: "Produits surgelés",
      frozen: [
        { key: "poissons", name: "Poissons", desc: "Poissons entiers et filets surgelés en mer ou à quai, calibre et glaçure maîtrisés." },
        { key: "viandes", name: "Viandes", desc: "Viandes bovines, ovines et volailles, abattage contrôlé, découpe selon cahier des charges." },
        { key: "autres", name: "Autres denrées congelées", desc: "Légumes, fruits et préparations congelées IQF pour l'industrie et la distribution." },
      ],
      cold: {
        title: "Logistique frigorifique intégrée",
        text: "Conteneurs reefer, entrepôts frigorifiques et suivi de température en continu : la chaîne du froid est maintenue à -25°C du chargement à la livraison, avec relevés transmis à chaque étape.",
        points: ["Conteneurs reefer contrôlés", "Entrepôts frigorifiques partenaires", "Relevés de température en continu"],
      },
    },
    product: {
      back: "Retour au catalogue",
      notFound: "Produit introuvable.",
      categoryRaw: "Matière première agricole",
      categoryFrozen: "Produit surgelé",
      specsTitle: "Spécifications",
      packingTitle: "Conditionnement & expédition",
      quoteTitle: "Demande de cotation",
      quoteIntro: "Indiquez vos besoins : volumes, destination, incoterm. Réponse sous 24 heures ouvrées.",
      quantity: "Quantité estimée",
      quantityPh: "Ex : 5 conteneurs 40', 100 tonnes…",
      messagePh: "Précisez la destination, l'incoterm souhaité, le calendrier…",
      submit: "Envoyer la demande de cotation",
    },
    trade: {
      overline: "Import / Export & Logistique",
      title: "Nos routes relient quatre continents",
      intro:
        "De l'Afrique de l'Ouest vers l'Europe, l'Asie et les Amériques : nous orchestrons le transport maritime, le dédouanement et la livraison finale avec nos partenaires armateurs et transitaires.",
      zonesTitle: "Zones de commerce",
      zones: [
        { name: "Afrique de l'Ouest", role: "Origine — sourcing & collecte" },
        { name: "Europe", role: "Import — distribution" },
        { name: "Asie", role: "Import / Export — négoce" },
        { name: "Amériques", role: "Export — marchés de destination" },
      ],
      certsTitle: "Normes & certifications",
      certs: ["HACCP", "ISO 22000", "Certificats phytosanitaires", "Inspections SGS / Bureau Veritas"],
      fleetTitle: "Partenaires logistiques",
      fleetText:
        "Armateurs, transitaires agréés et compagnies d'assurance cargo : un réseau éprouvé qui sécurise chaque expédition, du documentaire au physique.",
    },
    why: {
      overline: "Pourquoi nous choisir",
      title: "Quatre raisons de nous confier vos cargaisons",
      cells: [
        { title: "Réseau international", text: "Producteurs, armateurs et acheteurs sur quatre continents — un réseau construit cargaison après cargaison.", stat: "15+ pays" },
        { title: "Contrôle qualité", text: "Inspection à l'origine et à destination, analyses indépendantes, conformité documentaire complète.", stat: "100% lots inspectés" },
        { title: "Chaîne du froid maîtrisée", text: "Températures monitorées en continu, alertes en temps réel, relevés transmis au client.", stat: "-25°C garantis" },
        { title: "Délais respectés", text: "Planification rigoureuse des fenêtres portuaires et des correspondances, sans surprise.", stat: "98% à l'heure" },
      ],
    },
    testimonials: {
      overline: "Ils nous font confiance",
      title: "La parole à nos partenaires",
      items: [
        {
          quote: "ORIS a sécurisé notre approvisionnement en noix de cajou sur trois campagnes consécutives. Une rigueur documentaire remarquable.",
          name: "Directeur des achats",
          role: "Groupe agroalimentaire — Casablanca",
        },
        {
          quote: "Leurs relevés de température en continu ont transformé notre contrôle qualité à réception. Aucune non-conformité depuis deux ans.",
          name: "Responsable supply chain",
          role: "Distributeur de produits surgelés — Rotterdam",
        },
        {
          quote: "Des fenêtres de livraison tenues au jour près, même en haute saison. Un partenaire sur lequel on peut planifier.",
          name: "Importateur",
          role: "Négoce de céréales — Hô Chi Minh-Ville",
        },
      ],
      partnersTitle: "Ils commercent avec ORIS",
      partners: ["SAHEL AGRO", "ATLANTIC FOODS", "NOVA TRADE", "GOLFE NÉGOCE", "IVOIRE CACAO", "MERIDIAN COLD"],
    },
    contact: {
      overline: "Contact",
      title: "Parlons de votre prochaine cargaison",
      intro: "Un besoin d'approvisionnement, une cotation, un partenariat ? Notre équipe répond sous 24 heures ouvrées.",
      form: {
        name: "Nom complet",
        company: "Société",
        email: "Adresse email",
        subject: "Sujet",
        subjectOptions: ["Matières premières agricoles", "Produits surgelés", "Logistique frigorifique", "Autre demande"],
        message: "Votre message",
        submit: "Envoyer la demande",
        sending: "Envoi en cours…",
        success: "Votre message a bien été enregistré. Nous revenons vers vous rapidement.",
        error: "Une erreur est survenue. Merci de réessayer.",
        required: "Ce champ est requis",
      },
      info: {
        offices: [
          { label: "Bureau Côte d'Ivoire", value: "Marcory Résidentiel, 22 rues Cérès — Abidjan" },
          { label: "Bureau France", value: "1 rue Jacqueline Auriol, 13700 Marignane" },
        ],
        phones: [
          { label: "Côte d'Ivoire", value: "+225 07 10 35 16 17" },
          { label: "France", value: "+33 7 75 05 74 07" },
        ],
        emailLabel: "Email",
        email: "contact@oris-international.com",
      },
    },
    footer: {
      tagline: "Négoce international de matières premières agricoles et de produits surgelés.",
      navTitle: "Navigation",
      legalTitle: "Informations",
      legal: ["Mentions légales", "Politique de confidentialité"],
      rights: "© 2026 ORIS INTERNATIONAL — Tous droits réservés",
      admin: "Espace messages",
    },
    admin: {
      title: "Messages reçus",
      loginTitle: "Espace messages",
      loginIntro: "Cet espace est réservé à l'équipe ORIS INTERNATIONAL.",
      passwordLabel: "Mot de passe",
      loginButton: "Se connecter",
      loggingIn: "Connexion…",
      loginError: "Mot de passe incorrect. Réessayez.",
      sessionExpired: "Session expirée. Reconnectez-vous.",
      logout: "Déconnexion",
      reply: "Répondre",
      replyLabel: "Votre réponse (envoyée par email)",
      replyPh: "Rédigez votre réponse…",
      replySend: "Envoyer la réponse",
      replySending: "Envoi…",
      replySuccess: "Réponse envoyée par email au client.",
      replyError: "Échec de l'envoi de la réponse.",
      replyCancel: "Annuler",
      statusLabel: "Statut",
      status: { nouveau: "Nouveau", en_cours: "En cours", traite: "Traité" },
      empty: "Aucun message pour le moment.",
      back: "Retour au site",
      refresh: "Actualiser",
      count: "message(s)",
    },
  },
  en: {
    nav: {
      home: "Home",
      about: "About",
      products: "Products",
      trade: "Import / Export",
      contact: "Contact",
      cta: "Get in touch",
      menu: "Menu",
      close: "Close",
    },
    hero: {
      overline: "International trading — Raw commodities & frozen products",
      lines: ["Sourcing, trading,", "delivery: a supply chain", "mastered end to end."],
      sub: "From agricultural raw materials to frozen products, ORIS INTERNATIONAL secures your supply and supports your import-export operations worldwide.",
      ctaPrimary: "Explore our products",
      ctaSecondary: "Get in touch",
      scroll: "Scroll",
      stats: [
        { value: "2015", label: "Year founded" },
        { value: "50+", label: "Partner cooperatives" },
        { value: "2", label: "Offices — Abidjan & Marignane" },
        { value: "-25°C", label: "Controlled cold chain" },
      ],
    },
    marquee: [
      "Import / Export",
      "Certified quality",
      "Full traceability",
      "Mastered cold chain",
      "Sustainable sourcing",
      "Deadlines honored",
    ],
    about: {
      overline: "About — Our commitment",
      title: "A trading adventure serving Africa and the world",
      intro:
        "Founded in September 2015, ORIS INTERNATIONAL set itself a simple yet demanding mission: bringing producers, suppliers and international buyers together around the same standard of trust and quality.\n\nIt all starts with rigorous product selection at the source, directly with the local supply chains we know and follow closely. Then comes the fieldwork: quality control, negotiation, transaction structuring — every step is designed to protect our partners.\n\nOur expertise goes further. We support every shipment to its final destination, mastering the logistics and customs challenges of international trade — import and export alike.\n\nIt is this end-to-end mastery, combined with deep supply-chain knowledge and a constant commitment to transparency, that allows us to offer our partners reliable and lasting trade.",
      chapters: [
        {
          n: "01",
          title: "Our mission",
          text: "Sourcing, buying and reselling raw materials internationally. We select and trade agricultural raw materials and products for international markets — corn, cashew nuts, coffee, cocoa, shea and cashew shells — while developing a dedicated frozen-products business. From sourcing to delivery, we coordinate operations rigorously to guarantee reliability, supply continuity and respect for commercial commitments.",
        },
        {
          n: "02",
          title: "Our quality standards",
          text: "Quality starts at origin. We pay particular attention to product selection, compliance, and storage and transport conditions. Depending on the goods and the destination market's requirements, we ensure quality controls, compliance documents, inspections and preservation conditions are in place — especially for products under the cold chain.",
        },
        {
          n: "03",
          title: "Our logistics expertise",
          text: "Mastering every step of the journey. Sea freight, standard or refrigerated containers, documentary coordination and shipment tracking: we streamline every operation to reduce risks and secure deadlines.",
        },
        {
          n: "04",
          title: "Our vision",
          text: "Building trade relationships that last. For ORIS INTERNATIONAL, trading is more than a transaction. We favor partnerships founded on transparency, responsiveness, trust and long-term value creation.",
        },
      ],
      imageCaption: "Logistics & export — every shipment secured",
    },
    products: {
      overline: "Our products",
      title: "From production to international trade",
      intro: "We work with around fifty cooperatives across the entire country. Two product lines, one single standard: quality at origin, safety throughout the journey.",
      rawLabel: "Agricultural raw materials",
      fiche: "View details",
      raw: [
        { key: "mais", name: "Corn", desc: "Grain corn for human and animal consumption, dried and graded to your specifications." },
        { key: "cajou", name: "Cashew nuts", desc: "Raw in-shell cashew nuts, collected from producers in Côte d'Ivoire." },
        { key: "cafe", name: "Coffee", desc: "Arabica and robusta, lots traced back to the cooperative, controlled cup profiles." },
        { key: "cacao", name: "Cocoa", desc: "Fermented and dried fine-flavor beans, certification available on request." },
        { key: "riz", name: "Rice", desc: "White, parboiled and fragrant rice, packed in 25 to 50 kg bags for export." },
        { key: "karite", name: "Pure shea nuts", desc: "Pure shea nuts from a fair-trade supply chain in Côte d'Ivoire." },
        { key: "coque", name: "Cashew shells", desc: "Cashew nut shells for energy and industrial recovery (CNSL)." },
      ],
      frozenLabel: "Frozen products",
      frozen: [
        { key: "poissons", name: "Fish", desc: "Whole fish and fillets frozen at sea or at dock, controlled sizing and glazing." },
        { key: "viandes", name: "Meats", desc: "Beef, lamb and poultry, controlled slaughter, cutting to specification." },
        { key: "autres", name: "Other frozen goods", desc: "IQF vegetables, fruits and prepared foods for industry and retail." },
      ],
      cold: {
        title: "Integrated cold logistics",
        text: "Reefer containers, cold storage warehouses and continuous temperature monitoring: the cold chain is held at -25°C from loading to delivery, with records shared at every step.",
        points: ["Controlled reefer containers", "Partner cold-storage facilities", "Continuous temperature records"],
      },
    },
    product: {
      back: "Back to the catalogue",
      notFound: "Product not found.",
      categoryRaw: "Agricultural raw material",
      categoryFrozen: "Frozen product",
      specsTitle: "Specifications",
      packingTitle: "Packaging & shipping",
      quoteTitle: "Request a quotation",
      quoteIntro: "Tell us your needs: volumes, destination, incoterm. Reply within 24 business hours.",
      quantity: "Estimated quantity",
      quantityPh: "E.g. 5 × 40' containers, 100 tonnes…",
      messagePh: "Specify destination, preferred incoterm, schedule…",
      submit: "Send quotation request",
    },
    trade: {
      overline: "Import / Export & Logistics",
      title: "Our routes connect four continents",
      intro:
        "From West Africa to Europe, Asia and the Americas: we orchestrate sea freight, customs clearance and final delivery with our carrier and freight-forwarding partners.",
      zonesTitle: "Trade zones",
      zones: [
        { name: "West Africa", role: "Origin — sourcing & collection" },
        { name: "Europe", role: "Import — distribution" },
        { name: "Asia", role: "Import / Export — trading" },
        { name: "Americas", role: "Export — destination markets" },
      ],
      certsTitle: "Standards & certifications",
      certs: ["HACCP", "ISO 22000", "Phytosanitary certificates", "SGS / Bureau Veritas inspections"],
      fleetTitle: "Logistics partners",
      fleetText:
        "Shipping lines, licensed forwarders and cargo insurers: a proven network securing every shipment, from paperwork to physical handling.",
    },
    why: {
      overline: "Why choose us",
      title: "Four reasons to trust us with your cargo",
      cells: [
        { title: "International network", text: "Producers, carriers and buyers across four continents — a network built shipment after shipment.", stat: "15+ countries" },
        { title: "Quality control", text: "Inspection at origin and destination, independent analyses, full documentary compliance.", stat: "100% lots inspected" },
        { title: "Mastered cold chain", text: "Continuously monitored temperatures, real-time alerts, records shared with the client.", stat: "-25°C guaranteed" },
        { title: "Deadlines honored", text: "Rigorous planning of port windows and connections, without surprises.", stat: "98% on time" },
      ],
    },
    testimonials: {
      overline: "They trust us",
      title: "In our partners' words",
      items: [
        {
          quote: "ORIS secured our cashew supply for three consecutive seasons. Remarkable documentary rigor.",
          name: "Head of Procurement",
          role: "Agri-food group — Casablanca",
        },
        {
          quote: "Their continuous temperature records transformed our inbound quality control. Zero non-conformity in two years.",
          name: "Supply Chain Manager",
          role: "Frozen food distributor — Rotterdam",
        },
        {
          quote: "Delivery windows kept to the day, even in peak season. A partner you can plan on.",
          name: "Importer",
          role: "Grain trading house — Ho Chi Minh City",
        },
      ],
      partnersTitle: "Trading with ORIS",
      partners: ["SAHEL AGRO", "ATLANTIC FOODS", "NOVA TRADE", "GOLFE NÉGOCE", "IVOIRE CACAO", "MERIDIAN COLD"],
    },
    contact: {
      overline: "Contact",
      title: "Let's talk about your next shipment",
      intro: "A sourcing need, a quotation, a partnership? Our team replies within 24 business hours.",
      form: {
        name: "Full name",
        company: "Company",
        email: "Email address",
        subject: "Subject",
        subjectOptions: ["Agricultural raw materials", "Frozen products", "Cold logistics", "Other request"],
        message: "Your message",
        submit: "Send request",
        sending: "Sending…",
        success: "Your message has been recorded. We will get back to you shortly.",
        error: "Something went wrong. Please try again.",
        required: "This field is required",
      },
      info: {
        offices: [
          { label: "Côte d'Ivoire office", value: "Marcory Résidentiel, 22 rues Cérès — Abidjan" },
          { label: "France office", value: "1 rue Jacqueline Auriol, 13700 Marignane" },
        ],
        phones: [
          { label: "Côte d'Ivoire", value: "+225 07 10 35 16 17" },
          { label: "France", value: "+33 7 75 05 74 07" },
        ],
        emailLabel: "Email",
        email: "contact@oris-international.com",
      },
    },
    footer: {
      tagline: "International trading of agricultural raw materials and frozen products.",
      navTitle: "Navigation",
      legalTitle: "Information",
      legal: ["Legal notice", "Privacy policy"],
      rights: "© 2026 ORIS INTERNATIONAL — All rights reserved",
      admin: "Message board",
    },
    admin: {
      title: "Received messages",
      loginTitle: "Message board",
      loginIntro: "This area is reserved for the ORIS INTERNATIONAL team.",
      passwordLabel: "Password",
      loginButton: "Sign in",
      loggingIn: "Signing in…",
      loginError: "Incorrect password. Try again.",
      sessionExpired: "Session expired. Please sign in again.",
      logout: "Sign out",
      reply: "Reply",
      replyLabel: "Your reply (sent by email)",
      replyPh: "Write your reply…",
      replySend: "Send reply",
      replySending: "Sending…",
      replySuccess: "Reply emailed to the client.",
      replyError: "Failed to send the reply.",
      replyCancel: "Cancel",
      statusLabel: "Status",
      status: { nouveau: "New", en_cours: "In progress", traite: "Done" },
      empty: "No messages yet.",
      back: "Back to the site",
      refresh: "Refresh",
      count: "message(s)",
    },
  },
};
